import React, { useMemo } from 'react';
import DataTable from 'react-data-table-component-footer';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../../../utils/functions';

const TierLogs = ({ isActive, logs = [], venueTypes = [] }) => {
    const { t } = useTranslation();

    const venueTypeLookup = useMemo(() => {
        const map = new Map();
        (venueTypes || []).forEach((type) => {
            map.set(type.id, type.name || type.label || type.description);
        });
        return map;
    }, [venueTypes]);

    const formatActionLabel = (action) => {
        const key = `sections.management.section.pricing.tierLogs.actions.${action}`;
        const translated = t(key);
        return translated === key ? action : translated;
    };

    const columns = useMemo(
        () => [
            {
                name: t(
                    'sections.management.section.pricing.tierLogs.columns.date'
                ),
                sortable: true,
                selector: (row) => row.createdAt,
                cell: (row) => {
                    if (!row.createdAt) return '-';
                    const date = new Date(row.createdAt);
                    return date.toLocaleString();
                },
            },
            {
                name: t(
                    'sections.management.section.pricing.tierLogs.columns.action'
                ),
                selector: (row) => row.action,
                sortable: true,
                cell: (row) => formatActionLabel(row.action),
            },
            {
                name: t(
                    'sections.management.section.pricing.tierLogs.columns.tier'
                ),
                sortable: true,
                selector: (row) => row.tier,
                cell: (row) => (row.tier ? `#${row.tier}` : '-'),
            },
            {
                name: t(
                    'sections.management.section.pricing.tierLogs.columns.venueType'
                ),
                selector: (row) => row.venueTypeId,
                sortable: true,
                cell: (row) => {
                    const resolved =
                        row.venueTypeName ||
                        venueTypeLookup.get(row.venueTypeId) ||
                        row.screenType;
                    return resolved || '-';
                },
            },
            {
                name: t(
                    'sections.management.section.pricing.tierLogs.columns.baseCpm'
                ),
                selector: (row) => row.baseCpm,
                sortable: true,
                cell: (row) =>
                    typeof row.baseCpm === 'number'
                        ? formatNumber(row.baseCpm, 'properCurrency', null, {
                              currency: 'USD',
                          })
                        : '-',
            },
            {
                name: t(
                    'sections.management.section.pricing.tierLogs.columns.user'
                ),
                selector: (row) => row.userName || row.userEmail,
                cell: (row) => {
                    if (!row.userName && !row.userEmail) return '-';
                    if (row.userName && row.userEmail) {
                        return `${row.userName} (${row.userEmail})`;
                    }
                    return row.userName || row.userEmail;
                },
            },
            {
                name: t(
                    'sections.management.section.pricing.tierLogs.columns.notes'
                ),
                selector: (row) => row.notes || row.reason,
                grow: 2,
                cell: (row) => {
                    const notes = row.notes || row.reason;
                    return notes ? (
                        <span className="whitespace-pre-wrap text-sm text-gray-700">
                            {notes}
                        </span>
                    ) : (
                        '-'
                    );
                },
            },
        ],
        [t, venueTypeLookup]
    );

    const paginationOptions = {
        rowsPerPageText: t('sections.management.section.pricing.row-per-page'),
        rangeSeparatorText: t('sections.management.section.pricing.of'),
        noRowsPerPage: false,
    };

    return (
        <div
            className={`${
                isActive ? 'block' : 'hidden'
            } px-4 w-full md:w-10/12 overflow-x-auto`}
            data-testid="tier-logs-container">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">
                    {t('sections.management.section.pricing.tierLogs.title')}
                </h2>
            </div>
            {logs.length === 0 ? (
                <p className="text-sm text-gray-500">
                    {t('sections.management.section.pricing.tierLogs.empty')}
                </p>
            ) : (
                <DataTable
                    columns={columns}
                    data={logs}
                    pagination
                    paginationPerPage={10}
                    noDataComponent={t(
                        'common.components.react-data-table.no-data'
                    )}
                    paginationComponentOptions={paginationOptions}
                    striped
                />
            )}
        </div>
    );
};

export default TierLogs;
